import { InfoIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import { useCallback } from 'react';
import { ColorHex } from '../../constants';
import { TextButton } from '../Typography';
import { useTabs } from './TabsContext';
import type { TabId } from './types';

export interface TabProps {
  id: TabId;
  label: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  notificationDot?: boolean;
}

export const Tab = ({
  className,
  disabled,
  id,
  label,
  error,
  notificationDot,
}: TabProps) => {
  const { selectedTabId, changeTab } = useTabs();

  const isSelected = selectedTabId === id;
  const isActive = disabled || isSelected;

  const onClickHandler = useCallback(() => {
    if (isActive) {
      return;
    }
    changeTab(id);
  }, [changeTab, id, isActive]);

  const dotColor = disabled
    ? 'bg-ds-natural-150'
    : isSelected
      ? 'bg-ds-primary'
      : 'bg-ds-natural-400';

  return (
    <li
      onClick={onClickHandler}
      className={classNames(className, 'mr-1 font-medium', {
        'cursor-pointer': !isActive,
        'cursor-default': isActive,
        'border-b-3 border-ds-primary': isSelected,
      })}
    >
      <div
        className={
          disabled
            ? 'flex items-center hover:bg-none rounded-md px-4 py-2'
            : 'flex hover:bg-ds-natural-50 rounded-md px-4 py-2'
        }
      >
        <div className="flex items-center">
          <div
            className={classNames('flex', {
              'text-ds-natural-150 hover:text-ds-natural-150': disabled,
              'text-ds-primary-300 hover:text-ds-primary-300': isSelected,
              'text-ds-natural-300 hover:text-ds-natural-500':
                !isSelected && !disabled,
            })}
          >
            <TextButton
              label={label}
              color="text-current"
            />
            {notificationDot && (
              <div
                className={classNames(
                  dotColor,
                  'ml-2 w-ds-dot h-ds-dot rounded-ds-dot'
                )}
              />
            )}
          </div>
          {error && (
            <div className="absolute left-32">
              <InfoIcon
                color={ColorHex.danger}
                size={16}
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
};
